import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Megaphone, Plus, Pin, PinOff, Trash2, Eye, Bell, Calendar,
  Users, GraduationCap, UserCog, AlertTriangle, CheckCircle, Send
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AnnouncementsPage = () => {
  const { getAuthHeader, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  
  const [form, setForm] = useState({
    title: '',
    content: '',
    notice_type: 'general',
    priority: 'normal',
    target_type: 'all',
    target_classes: [],
    target_users: [],
    target_roles: [],
    is_pinned: false,
    send_push: true,
    send_email: false,
    send_whatsapp: false
  });

  const isAdmin = user?.role === 'school_admin' || user?.role === 'super_admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      const [annRes, classesRes] = await Promise.all([
        axios.get(`${API_URL}/api/announcements`, { headers }),
        axios.get(`${API_URL}/api/classes`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setAnnouncements(annRes.data || []);
      setClasses(classesRes.data || []);
      
      if (isAdmin) {
        const [studentsRes, staffRes] = await Promise.all([
          axios.get(`${API_URL}/api/behavior/students-list`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/teachers`, { headers }).catch(() => ({ data: [] }))
        ]);
        const studentsData = studentsRes.data?.items || studentsRes.data || [];
        setStudents(studentsData);
        setStaff(staffRes.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.title || !form.content) {
      toast.error('Title and content are required');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/announcements`, form, { headers: getAuthHeader() });
      toast.success('Announcement sent successfully!');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to create announcement');
    }
  };

  const resetForm = () => {
    setForm({
      title: '',
      content: '',
      notice_type: 'general',
      priority: 'normal',
      target_type: 'all',
      target_classes: [],
      target_users: [],
      target_roles: [],
      is_pinned: false,
      send_push: true,
      send_email: false,
      send_whatsapp: false
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await axios.delete(`${API_URL}/api/announcements/${id}`, { headers: getAuthHeader() });
      toast.success('Announcement deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleTogglePin = async (id) => {
    try {
      const res = await axios.patch(`${API_URL}/api/announcements/${id}/pin`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const viewDetail = async (announcement) => {
    try {
      const res = await axios.get(`${API_URL}/api/announcements/${announcement.id}`, { headers: getAuthHeader() });
      setSelectedAnnouncement(res.data);
      setDetailDialog(true);
      
      // Mark as read
      if (!res.data.is_read) {
        await axios.post(`${API_URL}/api/announcements/${announcement.id}/mark-read`, {}, { headers: getAuthHeader() });
      }
    } catch (error) {
      toast.error('Failed to load details');
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      general: Bell,
      urgent: AlertTriangle,
      event: Calendar,
      exam: GraduationCap,
      fee: Users,
      holiday: Calendar
    };
    return icons[type] || Bell;
  };

  const getTypeColor = (type) => {
    const colors = {
      general: 'bg-blue-100 text-blue-800',
      urgent: 'bg-red-100 text-red-800',
      event: 'bg-purple-100 text-purple-800',
      exam: 'bg-amber-100 text-amber-800',
      fee: 'bg-green-100 text-green-800',
      holiday: 'bg-pink-100 text-pink-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-600',
      normal: 'bg-blue-100 text-blue-600',
      high: 'bg-orange-100 text-orange-600',
      urgent: 'bg-red-100 text-red-600'
    };
    return colors[priority] || '';
  };

  const getTargetLabel = (ann) => {
    if (ann.target_type === 'all') return 'Everyone';
    if (ann.target_type === 'students') return 'All Students';
    if (ann.target_type === 'staff') return 'All Staff';
    if (ann.target_type === 'teachers') return 'Teachers';
    if (ann.target_type === 'parents') return 'Parents';
    if (ann.target_type === 'class') return `${ann.target_classes?.length || 0} Classes`;
    if (ann.target_type === 'individual') return `${ann.recipient_count || 0} Users`;
    return ann.target_type;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Megaphone className="h-6 w-6" /> Announcements
            </h1>
            <p className="text-muted-foreground">Send targeted notices to students, staff, and parents</p>
          </div>
          {isAdmin && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-announcement-btn">
                  <Plus className="h-4 w-4 mr-2" /> New Announcement
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Announcement</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {/* Title */}
                  <div className="space-y-2">
                    <Label>Title *</Label>
                    <Input
                      value={form.title}
                      onChange={(e) => setForm({...form, title: e.target.value})}
                      placeholder="Announcement title"
                    />
                  </div>

                  {/* Content */}
                  <div className="space-y-2">
                    <Label>Content *</Label>
                    <Textarea
                      value={form.content}
                      onChange={(e) => setForm({...form, content: e.target.value})}
                      placeholder="Write your announcement..."
                      rows={4}
                    />
                  </div>

                  {/* Type and Priority */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select value={form.notice_type} onValueChange={(v) => setForm({...form, notice_type: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">📢 General</SelectItem>
                          <SelectItem value="urgent">🚨 Urgent</SelectItem>
                          <SelectItem value="event">📅 Event</SelectItem>
                          <SelectItem value="exam">📝 Exam</SelectItem>
                          <SelectItem value="fee">💰 Fee Related</SelectItem>
                          <SelectItem value="holiday">🏖️ Holiday</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Priority</Label>
                      <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Target Audience */}
                  <div className="space-y-2">
                    <Label>Send To</Label>
                    <Select value={form.target_type} onValueChange={(v) => setForm({...form, target_type: v, target_classes: [], target_users: []})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">👥 Everyone (All Users)</SelectItem>
                        <SelectItem value="students">🎓 All Students</SelectItem>
                        <SelectItem value="class">📚 Specific Classes</SelectItem>
                        <SelectItem value="individual">👤 Individual Users</SelectItem>
                        <SelectItem value="staff">👔 All Staff</SelectItem>
                        <SelectItem value="teachers">👨‍🏫 Teachers Only</SelectItem>
                        <SelectItem value="parents">👨‍👩‍👧 Parents Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Class Selection */}
                  {form.target_type === 'class' && (
                    <div className="space-y-2">
                      <Label>Select Classes</Label>
                      <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto p-2 border rounded">
                        {classes.map(cls => (
                          <label key={cls.id} className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={form.target_classes.includes(cls.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setForm({...form, target_classes: [...form.target_classes, cls.id]});
                                } else {
                                  setForm({...form, target_classes: form.target_classes.filter(id => id !== cls.id)});
                                }
                              }}
                            />
                            <span className="text-sm">{cls.name} {cls.section}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Individual User Selection */}
                  {form.target_type === 'individual' && (
                    <div className="space-y-2">
                      <Label>Select Users</Label>
                      <div className="max-h-40 overflow-y-auto p-2 border rounded space-y-1">
                        {[...students, ...staff].map(u => (
                          <label key={u.id} className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={form.target_users.includes(u.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setForm({...form, target_users: [...form.target_users, u.id]});
                                } else {
                                  setForm({...form, target_users: form.target_users.filter(id => id !== u.id)});
                                }
                              }}
                            />
                            <span className="text-sm">{u.name} <span className="text-muted-foreground">({u.role || 'Student'})</span></span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Options */}
                  <div className="space-y-3 pt-2 border-t">
                    <Label>Delivery Options</Label>
                    <div className="flex flex-wrap gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={form.is_pinned}
                          onCheckedChange={(checked) => setForm({...form, is_pinned: checked})}
                        />
                        <span className="text-sm">📌 Pin to top</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={form.send_push}
                          onCheckedChange={(checked) => setForm({...form, send_push: checked})}
                        />
                        <span className="text-sm">🔔 In-app notification</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={form.send_email}
                          onCheckedChange={(checked) => setForm({...form, send_email: checked})}
                        />
                        <span className="text-sm">📧 Email</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={form.send_whatsapp}
                          onCheckedChange={(checked) => setForm({...form, send_whatsapp: checked})}
                        />
                        <span className="text-sm">💬 WhatsApp</span>
                      </label>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
                  <Button onClick={handleCreate}>
                    <Send className="h-4 w-4 mr-2" /> Send Announcement
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Announcements List */}
        <Card>
          <CardHeader>
            <CardTitle>All Announcements</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : announcements.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Megaphone className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>No announcements yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {announcements.map((ann) => {
                  const TypeIcon = getTypeIcon(ann.notice_type);
                  return (
                    <motion.div
                      key={ann.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-4 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors ${
                        ann.is_pinned ? 'border-amber-300 bg-amber-50/50' : ''
                      } ${!ann.is_read ? 'border-l-4 border-l-blue-500' : ''}`}
                      onClick={() => viewDetail(ann)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`p-2 rounded-lg ${getTypeColor(ann.notice_type)}`}>
                            <TypeIcon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              {ann.is_pinned && <Pin className="h-4 w-4 text-amber-500" />}
                              <h3 className="font-semibold truncate">{ann.title}</h3>
                              {!ann.is_read && <Badge className="bg-blue-500 text-xs">New</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{ann.content}</p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                              <Badge variant="outline" className={getPriorityColor(ann.priority)}>{ann.priority}</Badge>
                              <span>To: {getTargetLabel(ann)}</span>
                              <span>{new Date(ann.created_at).toLocaleDateString()}</span>
                              {isAdmin && (
                                <span className="flex items-center gap-1">
                                  <Eye className="h-3 w-3" /> {ann.read_count || 0}/{ann.recipient_count || 0}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {isAdmin && (
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" onClick={() => handleTogglePin(ann.id)}>
                              {ann.is_pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(ann.id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedAnnouncement?.is_pinned && <Pin className="h-4 w-4 text-amber-500" />}
                {selectedAnnouncement?.title}
              </DialogTitle>
            </DialogHeader>
            {selectedAnnouncement && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Badge className={getTypeColor(selectedAnnouncement.notice_type)}>
                    {selectedAnnouncement.notice_type}
                  </Badge>
                  <Badge variant="outline" className={getPriorityColor(selectedAnnouncement.priority)}>
                    {selectedAnnouncement.priority} priority
                  </Badge>
                </div>
                
                <div className="prose prose-sm max-w-none">
                  <p className="whitespace-pre-wrap">{selectedAnnouncement.content}</p>
                </div>
                
                <div className="text-sm text-muted-foreground border-t pt-4 space-y-1">
                  <p>📅 Posted: {new Date(selectedAnnouncement.created_at).toLocaleString()}</p>
                  <p>👤 By: {selectedAnnouncement.created_by_name}</p>
                  <p>📨 Sent to: {getTargetLabel(selectedAnnouncement)} ({selectedAnnouncement.recipient_count} recipients)</p>
                  {isAdmin && (
                    <p>👁️ Read by: {selectedAnnouncement.read_count} users</p>
                  )}
                </div>

                {selectedAnnouncement.attachment_url && (
                  <div className="pt-2">
                    <a 
                      href={selectedAnnouncement.attachment_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 underline text-sm"
                    >
                      📎 {selectedAnnouncement.attachment_name || 'Download Attachment'}
                    </a>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AnnouncementsPage;
